import { Pill, Droplet, Syringe, Wallet, Calendar, MessageSquare, Phone, Mail, Heart } from 'lucide-react';

interface PreferencesStepProps {
  programRecommended: string;
  consultationMethod: string;
  refillFrequency: string;
  budget: string;
  additionalPreferences: string;
  updateFormData: (field: string, value: string) => void;
}

const DELIVERY_METHODS = [
  { 
    value: 'oral', 
    label: 'Oral (Pills/Capsules)', 
    icon: Pill,
    description: 'Convenient daily tablets or capsules',
    pros: 'Easy to take, familiar format',
    cons: 'Must remember daily, first-pass metabolism'
  },
  { 
    value: 'topical', 
    label: 'Topical (Creams/Gels)', 
    icon: Droplet,
    description: 'Applied to skin daily',
    pros: 'Steady absorption, bypass digestive system',
    cons: 'Can be messy, skin irritation possible'
  },
  { 
    value: 'sublingual', 
    label: 'Sublingual (Under tongue)', 
    icon: Droplet,
    description: 'Dissolves under tongue',
    pros: 'Fast absorption, consistent dosing',
    cons: 'Must hold in place, taste sensitivity'
  },
  { 
    value: 'injection', 
    label: 'Injections (Self-administered)', 
    icon: Syringe,
    description: 'Weekly or bi-weekly injections',
    pros: 'Most effective absorption, less frequent',
    cons: 'Requires injection skill, needle anxiety'
  },
  { 
    value: 'patch', 
    label: 'Transdermal Patch', 
    icon: Droplet,
    description: 'Applied to skin, changed weekly',
    pros: 'Steady levels, convenient',
    cons: 'Can fall off, skin irritation'
  },
  { 
    value: 'no_preference', 
    label: 'No Preference / Provider Choice', 
    icon: Heart,
    description: 'Let your provider recommend',
    pros: 'Expert guidance based on your needs',
    cons: 'Less control over method'
  },
];

const CONSULTATION_PREFERENCES = [
  { value: 'video', label: 'Video Call (Telehealth)', icon: MessageSquare },
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'in_person', label: 'In-Person Visit', icon: Heart },
  { value: 'text', label: 'Secure Messaging / Text', icon: Mail },
];

export default function PreferencesStep({ 
  programRecommended, 
  consultationMethod,
  refillFrequency,
  budget,
  additionalPreferences,
  updateFormData 
}: PreferencesStepProps) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 6 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          Treatment Preferences
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Help us personalize your hormone therapy experience
        </p>
      </div>

      {/* Delivery Method Preference */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Pill className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Preferred Delivery Method
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: '#C1C9D2' }}>
          Select your preferred method for hormone delivery (you can discuss alternatives with your provider)
        </p>
        
        <div className="space-y-3">
          {DELIVERY_METHODS.map((method) => {
            const Icon = method.icon;
            const isSelected = programRecommended === method.value;
            
            return (
              <button
                key={method.value}
                type="button"
                onClick={() => updateFormData('programRecommended', method.value)}
                className="w-full p-4 rounded-lg text-left transition-all"
                style={{
                  background: isSelected 
                    ? 'rgba(47, 255, 199, 0.1)' 
                    : 'rgba(255, 255, 255, 0.02)',
                  border: isSelected 
                    ? '2px solid #2FFFC7' 
                    : '1px solid rgba(255, 255, 255, 0.05)',
                }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="p-2 rounded-lg flex-shrink-0"
                    style={{
                      background: isSelected 
                        ? 'rgba(47, 255, 199, 0.2)' 
                        : 'rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <Icon 
                      className="h-5 w-5" 
                      style={{ color: isSelected ? '#2FFFC7' : '#9DA9B8' }} 
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold" style={{ color: '#E6E9F0' }}>
                        {method.label}
                      </h4>
                      {isSelected && (
                        <div 
                          className="h-5 w-5 rounded-full flex items-center justify-center"
                          style={{ background: '#2FFFC7' }}
                        >
                          <div className="h-2 w-2 rounded-full bg-black" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm mb-2" style={{ color: '#C1C9D2' }}>
                      {method.description}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span style={{ color: '#10B981' }}>✓ {method.pros}</span>
                      </div>
                      <div>
                        <span style={{ color: '#F59E0B' }}>⚠ {method.cons}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Consultation Preference */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Consultation Method
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: '#C1C9D2' }}>
          How would you prefer to consult with your provider?
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {CONSULTATION_PREFERENCES.map((pref) => {
            const Icon = pref.icon;
            const isSelected = consultationMethod === pref.value;
            return (
              <button
                key={pref.value}
                type="button"
                onClick={() => updateFormData('consultationMethod', pref.value)}
                className="p-4 rounded-lg text-left transition-all flex items-center gap-3 w-full"
                style={{
                  background: isSelected ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                  border: isSelected ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                }}
              >
                <Icon className="h-5 w-5 flex-shrink-0" style={{ color: isSelected ? '#2FFFC7' : '#9DA9B8' }} />
                <span className="text-sm font-medium flex-1" style={{ color: '#E6E9F0' }}>
                  {pref.label}
                </span>
                {isSelected && (
                  <div 
                    className="ml-auto h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: '#2FFFC7' }}
                  >
                    <div className="h-2 w-2 rounded-full bg-black" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Frequency Preference */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Refill Frequency
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: '#C1C9D2' }}>
          How often would you prefer to receive medication refills?
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { value: 'monthly', label: 'Monthly (30 days)' },
            { value: 'quarterly', label: 'Quarterly (90 days)' },
            { value: 'flexible', label: 'Flexible / As needed' },
          ].map((option) => {
            const isSelected = refillFrequency === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => updateFormData('refillFrequency', option.value)}
                className="px-4 py-3 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: isSelected ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                  border: isSelected ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                  color: '#E6E9F0',
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Budget Considerations */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Budget Considerations
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: '#C1C9D2' }}>
          What is your approximate monthly budget for hormone therapy?
        </p>
        <select
          value={budget}
          onChange={(e) => updateFormData('budget', e.target.value)}
          className="w-full px-4 py-3 rounded-lg text-sm appearance-none cursor-pointer"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
            color: budget ? '#000000' : '#6B7280',
          }}
        >
          <option value="" style={{ background: '#1a2332', color: '#C1C9D2' }}>
            Select budget range...
          </option>
          <option value="under_100" style={{ background: '#1a2332', color: '#E6E9F0' }}>
            Under $100/month
          </option>
          <option value="100_200" style={{ background: '#1a2332', color: '#E6E9F0' }}>
            $100-200/month
          </option>
          <option value="200_300" style={{ background: '#1a2332', color: '#E6E9F0' }}>
            $200-300/month
          </option>
          <option value="300_plus" style={{ background: '#1a2332', color: '#E6E9F0' }}>
            $300+/month
          </option>
          <option value="flexible" style={{ background: '#1a2332', color: '#E6E9F0' }}>
            Flexible - will invest in quality care
          </option>
        </select>
      </div>

      {/* Additional Notes */}
      <div>
        <h3 className="text-lg font-semibold mb-3" style={{ color: '#E6E9F0' }}>
          Additional Preferences or Questions
        </h3>
        <textarea
          value={additionalPreferences}
          onChange={(e) => updateFormData('additionalPreferences', e.target.value)}
          placeholder="Any other preferences, concerns, or questions for your provider..."
          rows={4}
          className="w-full px-4 py-3 rounded-lg text-sm"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
            color: '#000000',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Info Card */}
      <div 
        className="p-4 rounded-lg"
        style={{
          background: 'rgba(47, 255, 199, 0.05)',
          border: '1px solid rgba(47, 255, 199, 0.2)',
        }}
      >
        <p className="text-xs leading-relaxed" style={{ color: '#C1C9D2' }}>
          <strong style={{ color: '#2FFFC7' }}>Note:</strong> These preferences help guide your treatment plan, but your provider will work with you to find the best approach based on your individual health needs and goals.
        </p>
      </div>
    </div>
  );
}
